import { NextUI } from '@/components/NextUI';
import React, { useEffect, useState } from 'react';
import { hideModal } from '@/store/slices/modalSlice';
import { useAppDispatch } from '@/hooks/redux';
import { showToast } from '@/store/slices/toastActions';
import { usePlan } from '@/features/subscription/hooks/usePlan';
import { GoodbyStep } from '@/features/subscription/components/modals/cancel-sub/GoodbyStep';
import { ReasonStep } from '@/features/subscription/components/modals/cancel-sub/ReasonStep';
import { OpnionStep } from '@/features/subscription/components/modals/cancel-sub/OpnionStep';
import { FinalStep } from '@/features/subscription/components/modals/cancel-sub/FinalStep';
import { CancellationSteps } from '@/features/subscription/enum/cancellationSteps';
import { CancellationReasons } from '@/features/subscription/enum/cancellationReasons';
import { useTranslations } from 'next-intl';
import { emit } from '@/utils/emitter';
import { APP_EVENTS, logHubxEvent } from '@/app/providers/analytics';

const CANCELLATION_REASON_SOURCES: Record<CancellationReasons, string> = {
  [CancellationReasons.FEATURES_MISSING]: 'cancel_missing_feature',
  [CancellationReasons.NO_NEED]: 'cancel_no_need',
  [CancellationReasons.SWITCHING]: 'cancel_different__prod',
  [CancellationReasons.CANT_DO]: 'cancel_cannot_do',
  [CancellationReasons.TOO_EXPENSIVE]: 'cancel_expensive',
  [CancellationReasons.OTHER]: 'cancel_other',
};

function CancelSubscription() {
  const t = useTranslations('modals.cancel_subscription');
  const dispatch = useAppDispatch();
  const { planIsActive, planIsLoading, planError, cancelPlan } = usePlan();
  const [step, setStep] = useState<number>(CancellationSteps.Goodbye);
  const [reason, setReason] = useState<string>('');
  const [feedback, setFeedback] = useState<string>('');
  const [otherText, setOtherText] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  useEffect(() => {
    if (!planIsActive && step === 4 && !submissionError) {
      dispatch(
        showToast({
          text: t('success'),
          variant: 'success',
        })
      );
    }
  }, [planIsActive, step, dispatch, submissionError, t]);

  const getCancellationSource = () => {
    if (!reason) {
      return undefined;
    }

    return CANCELLATION_REASON_SOURCES[reason as CancellationReasons];
  };

  const handleReasonContinue = () => {
    const source = getCancellationSource();
    const normalizedOtherText = otherText.trim();

    logHubxEvent({
      eventName: APP_EVENTS.BTN_CANCEL_CONTINUE,
      ...(source ? { source } : {}),
      ...(reason === CancellationReasons.OTHER && normalizedOtherText
        ? {
            event_params: {
              reason: normalizedOtherText,
            },
          }
        : {}),
    });

    setStep(CancellationSteps.Opinion);
  };

  const handleCancelPlan = async () => {
    try {
      const source = getCancellationSource();
      const normalizedFeedback = feedback.trim();

      logHubxEvent({
        eventName: APP_EVENTS.BTN_CONFIRM_CANCEL,
        ...(source ? { source } : {}),
        ...(normalizedFeedback
          ? {
              event_params: {
                reason: normalizedFeedback,
              },
            }
          : {}),
      });

      setIsSubmitting(true);
      setSubmissionError(null);

      const response = await fetch(
        'https://strapi-947695455769.us-central1.run.app/api/cancel-subs',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            data: {
              issue: reason === CancellationReasons.OTHER ? otherText : reason,
              feedback,
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      setStep(4);
      void cancelPlan();
      emit('plan:cancel-plan');
    } catch (error) {
      console.error('Error submitting cancellation feedback:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to submit feedback';
      setSubmissionError(errorMessage);

      dispatch(
        showToast({
          text: t('error'),
          variant: 'danger',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelWithoutFeedback = () => {
    setStep(4);
    void cancelPlan();
  };

  const handleClose = () => {
    dispatch(hideModal());
  };

  const renderStep = () => {
    switch (step) {
      case CancellationSteps.Goodbye:
        return <GoodbyStep setStep={setStep} />;
      case CancellationSteps.Reason:
        return (
          <ReasonStep
            isSubmitting={isSubmitting}
            reason={reason}
            setReason={setReason}
            setStep={setStep}
            onContinue={handleReasonContinue}
            otherText={otherText}
            setOtherText={setOtherText}
          />
        );
      case CancellationSteps.Opinion:
        return (
          <OpnionStep
            isSubmitting={isSubmitting}
            feedback={feedback}
            submissionError={submissionError}
            planIsLoading={planIsLoading}
            setFeedback={setFeedback}
            setStep={setStep}
            cancelPlan={handleCancelPlan}
            cancelWithoutFeedback={handleCancelWithoutFeedback}
          />
        );
      case CancellationSteps.Final:
        return <FinalStep handleClose={handleClose} />;
      default:
        return null;
    }
  };

  return (
    <NextUI.Modal
      className="max-w-[500px] flex"
      classNames={{ content: 'py-8 !px-4 md:px-8 flex flex-col max-h-full' }}
      defaultOpen={true}
      name={'CancelSubscription'}
    >
      <div className="flex flex-col flex-1 w-full">
        {renderStep()}
        {planError && (
          <p className="text-red-500 text-[17px] mt-4">{planError.message}</p>
        )}
      </div>
    </NextUI.Modal>
  );
}

export default CancelSubscription;
